import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as InIcon,
  TrendingDown as OutIcon,
} from '@mui/icons-material';

const Stock = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    { field: 'codigo', headerName: 'Código', width: 100 },
    { field: 'descripcion', headerName: 'Descripción', width: 250, flex: 1, renderCell: (params) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
        <Typography variant="caption" color="text.secondary">{params.row.categoria}</Typography>
      </Box>
    )},
    { field: 'ubicacion', headerName: 'Ubicación', width: 120 },
    { field: 'stock', headerName: 'Stock', width: 120, renderCell: (params) => {
      const porcentaje = (params.value / params.row.stockMax) * 100;
      const isBajo = porcentaje < 25;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {isBajo && <WarningIcon fontSize="small" color="error" />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>{params.value} {params.row.unidad}</Typography>
            <LinearProgress
              variant="determinate"
              value={porcentaje}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[400], 0.2),
                '& .MuiLinearProgress-bar': {
                  bgcolor: isBajo ? theme.palette.error.main : theme.palette.success.main,
                },
              }}
            />
          </Box>
        </Box>
      );
    }},
    { field: 'stockMin', headerName: 'Mín', width: 70, align: 'center' },
    { field: 'stockMax', headerName: 'Máx', width: 70, align: 'center' },
    { field: 'estado', headerName: 'Estado', width: 120, renderCell: (params) => (
      <Chip label={params.value} size="small" color={params.value === 'OK' ? 'success' : params.value === 'Bajo' ? 'error' : 'warning'} />
    )},
    { field: 'acciones', headerName: 'Acciones', width: 120, sortable: false, renderCell: () => (
      <Box>
        <IconButton size="small" color="success"><InIcon fontSize="small" /></IconButton>
        <IconButton size="small" color="error"><OutIcon fontSize="small" /></IconButton>
        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
      </Box>
    )},
  ];

  const rows = [
    { id: 1, codigo: 'TUB-4', descripcion: 'Tuberías 4"', categoria: 'Tuberías', ubicacion: 'Depósito A', stock: 15, stockMin: 20, stockMax: 100, unidad: 'mts', estado: 'Bajo' },
    { id: 2, codigo: 'VAL-2', descripcion: 'Válvulas 2"', categoria: 'Válvulas', ubicacion: 'Depósito A', stock: 45, stockMin: 10, stockMax: 50, unidad: 'un', estado: 'OK' },
    { id: 3, codigo: 'CEM-50', descripcion: 'Cemento Clase G', categoria: 'Cemento', ubicacion: 'Depósito B', stock: 120, stockMin: 50, stockMax: 200, unidad: 'bolsas', estado: 'OK' },
    { id: 4, codigo: 'ACE-15', descripcion: 'Aceite Hidráulico', categoria: 'Lubricantes', ubicacion: 'Depósito A', stock: 8, stockMin: 15, stockMax: 50, unidad: 'lts', estado: 'Bajo' },
    { id: 5, codigo: 'EPP-CA', descripcion: 'Cascos de Seguridad', categoria: 'EPP', ubicacion: 'Depósito C', stock: 35, stockMin: 20, stockMax: 80, unidad: 'un', estado: 'OK' },
  ];

  const movimientosRecientes = [
    { fecha: '03/02/2026', tipo: 'Salida', item: 'Tuberías 4"', cantidad: 10, destino: 'Pozo XY-123' },
    { fecha: '02/02/2026', tipo: 'Entrada', item: 'Cemento Clase G', cantidad: 50, destino: 'Compra OC-0043' },
    { fecha: '01/02/2026', tipo: 'Salida', item: 'Válvulas 2"', cantidad: 5, destino: 'Pozo AB-456' },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Stock
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control de inventario con movimientos automáticos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<InIcon />}>
            Entrada
          </Button>
          <Button variant="outlined" color="error" startIcon={<OutIcon />}>
            Salida
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Nuevo Ítem
          </Button>
        </Box>
      </Box>

      {/* Alertas de Stock Bajo */}
      <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px solid ${theme.palette.error.main}` }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="error" />
            <Typography variant="body1" fontWeight={500} color="error.main">
              2 ítems con stock bajo: Tuberías 4", Aceite Hidráulico
            </Typography>
            <Button size="small" variant="outlined" color="error" sx={{ ml: 'auto' }}>
              Generar OC
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Tabla de Stock */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Inventario
              </Typography>
              <Box sx={{ height: 400 }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={5}
                  disableSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Movimientos Recientes */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Movimientos Recientes
              </Typography>
              {movimientosRecientes.map((mov, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: alpha(mov.tipo === 'Entrada' ? theme.palette.success.main : theme.palette.error.main, 0.05),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {mov.tipo === 'Entrada' ? <InIcon color="success" /> : <OutIcon color="error" />}
                      <Typography variant="body2" fontWeight={500}>{mov.item}</Typography>
                    </Box>
                    <Chip
                      label={`${mov.tipo === 'Entrada' ? '+' : '-'}${mov.cantidad}`}
                      size="small"
                      color={mov.tipo === 'Entrada' ? 'success' : 'error'}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {mov.fecha} - {mov.destino}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Ítem de Stock</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Código" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select label="Categoría">
                  <MenuItem value="tuberias">Tuberías</MenuItem>
                  <MenuItem value="valvulas">Válvulas</MenuItem>
                  <MenuItem value="cemento">Cemento</MenuItem>
                  <MenuItem value="lubricantes">Lubricantes</MenuItem>
                  <MenuItem value="epp">EPP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Stock Mínimo" type="number" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Stock Máximo" type="number" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Unidad" placeholder="un, mts, lts..." />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Ubicación</InputLabel>
                <Select label="Ubicación">
                  <MenuItem value="a">Depósito A</MenuItem>
                  <MenuItem value="b">Depósito B</MenuItem>
                  <MenuItem value="c">Depósito C</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stock;
